import { Users, ShieldAlert, Lock } from 'lucide-react'
import { GlassMetricCard } from './ui/glass'
import { useApiPolling } from '../hooks/useApiPolling'
import { formatMeasured } from '../lib/measured'
import { fetchPlatformStats, type PlatformStats } from '../lib/platformStats'

/**
 * W6 phase 2 — the only platform figures public pages may show.
 *
 * The owner's 2026-09-29 decision on public performance claims ("2.1B+ USD
 * protected", "140,000+ wallets", "99.97%") is "replace with measured or
 * remove". These three are live /api/stats fields, labelled for what they
 * count (see lib/platformStats.ts): activeUsers is count(*) FROM users, so it
 * is "Registered users", not active ones.
 */
export const MEASURED_STATS: { key: keyof PlatformStats; label: string; icon: typeof Users }[] = [
  { key: 'activeUsers', label: 'Registered users', icon: Users },
  { key: 'threatsBlocked24h', label: 'Threats blocked (24h)', icon: ShieldAlert },
  { key: 'totalTransactionsProtected', label: 'Encrypted transactions recorded', icon: Lock },
]

export const MEASURED_STATS_SOURCE = 'Live counts from /api/stats'

export function MeasuredPlatformStats({ variant = 'cards' }: { variant?: 'cards' | 'inline' }) {
  const { data, loading } = useApiPolling(fetchPlatformStats, { intervalMs: null })
  const value = (k: keyof PlatformStats) => formatMeasured(data?.[k] as number | null | undefined, { loading })

  if (variant === 'inline') {
    return (
      <div className="flex flex-wrap gap-6 text-sm" data-testid="measured-stats">
        {MEASURED_STATS.map(({ key, label }) => (
          <span key={key} className="text-white/40">
            <span className="text-accent font-semibold">{value(key)}</span> {label}
          </span>
        ))}
        <span className="text-white/25 text-xs self-center">{MEASURED_STATS_SOURCE}</span>
      </div>
    )
  }
  return (
    <div data-testid="measured-stats">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {MEASURED_STATS.map(({ key, label, icon }) => (
          <GlassMetricCard key={key} label={label} value={value(key)} icon={icon} />
        ))}
      </div>
      <p className="text-xs text-white/30 mt-2">{MEASURED_STATS_SOURCE}</p>
    </div>
  )
}
